import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './VerbalLearning.module.css';
import { ArrowBack } from '@mui/icons-material';
import { db } from "../../utils/firebase";
import { doc, setDoc, serverTimestamp, Timestamp } from "@firebase/firestore";
import { getUserId } from "../../utils/userId";
import { generateTestId, setCognitiveTestId, getCognitiveTestSetId } from "../../utils/testId";
import { v4 as uuidv4 } from "uuid";
import axios from 'axios';
import { getFunctions, httpsCallable } from "firebase/functions";

const wordsEn = [
  'Drum', 'Curtain', 'Bell', 'Coffee', 'School', 'Parent', 'Moon', 'Garden', 'Hat', 'Farmer',
  'Nose', 'Turkey', 'Color', 'House', 'River', 'Dest', 'Ranger', 'Bird', 'Shoe', 'Stove',
  'Mountain', 'Glasses', 'Towel', 'Cloud', 'Boat', 'Lamb', 'Gun', 'Pencil', 'Church', 'Fish'
];

const wordsKo = [
  '드럼', '커튼', '종', '커피', '학교', '부모', '달', '정원', '모자', '농부',
  '코', '칠면조', '색깔', '집', '강', '책상', '산지기', '새', '신발', '난로',
  '산', '안경', '수건', '구름', '배', '어린양', '총', '연필', '교회', '물고기'
];

const VerbalLearning: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { hospitalId } = useParams();
  const prefix = hospitalId ? `/${hospitalId}` : '';
  const [gameState, setGameState] = useState<'start' | 'playing' | 'results'>('start');
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [countdown, setCountdown] = useState(180);
  const [score, setScore] = useState(0);
  const [showVolumeCheck, setShowVolumeCheck] = useState(true);
  const [wordCountdown, setWordCountdown] = useState(5);
  const [selectedWords, setSelectedWords] = useState<string[]>([]);
  const [userInput, setUserInput] = useState('');
  const [correctWords, setCorrectWords] = useState<string[]>([]);
  const [enteredWords, setEnteredWords] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const [allWordsRead, setAllWordsRead] = useState(false);

  const audioCacheRef = useRef<{ [key: string]: string }>({});
  const audioPlayerRef = useRef<HTMLAudioElement>(new Audio());
  const [isAudioReady, setIsAudioReady] = useState(false);

  // 1. 공통 음성 재생 함수 (환경 변수 적용)
  const playWord = async (text: string, forceLang?: string) => {
    try {
      const currentLang = forceLang || (i18n.language.startsWith('ko') ? 'ko-KR' : 'en-US');
      const cacheKey = `${currentLang}_${text}`;
      let audioData = audioCacheRef.current[cacheKey];

      if (!audioData) {
        const functions = getFunctions();
        const getElevenLabsTTS = httpsCallable(functions, 'getElevenLabsTTS');
        
        const result = await getElevenLabsTTS({ text, lang: currentLang });
        
        const responseData = result.data as { audioData: string };
        audioData = `data:audio/mp3;base64,${responseData.audioData}`;
        audioCacheRef.current[cacheKey] = audioData;
      }
      
      const player = audioPlayerRef.current;
      player.src = audioData;
      await player.play();
      
      return new Promise((resolve) => {
        player.onended = resolve;
      });
    } catch (error) {
      console.error("음성 재생 실패, 기본 음성으로 대체:", error);
    }

    // return new Promise((resolve) => {
    //   const utterance = new SpeechSynthesisUtterance(text);
    //   utterance.lang = 'en-US';
    //   utterance.onend = () => resolve(true);
    //   window.speechSynthesis.speak(utterance);
    // });
  };

  // 2. 단어 선택 및 pre-fetch 로직 
  useEffect(() => {
    if (gameState === 'playing') {
      const currentWordList = i18n.language.startsWith('ko') ? wordsKo : wordsEn;
      const selected = [...currentWordList].sort(() => 0.5 - Math.random()).slice(0, 15);
      setSelectedWords(selected);

      const prefetchAudios = async () => {
        const functions = getFunctions();
        const getElevenLabsTTS = httpsCallable(functions, 'getElevenLabsTTS');
        const currentLang = i18n.language.startsWith('ko') ? 'ko-KR' : 'en-US';
        setIsAudioReady(false);

        for (const word of selected) {
        const cacheKey = `${currentLang}_${word}`;
        if (audioCacheRef.current[cacheKey]) continue; 
        
        try {
          console.log(`${word} 음성 생성 중...`);
          const result = await getElevenLabsTTS({ text: word, lang: currentLang });
          const responseData = result.data as { audioData: string };
          audioCacheRef.current[cacheKey] = `data:audio/mp3;base64,${responseData.audioData}`;
          
          // ElevenLabs 서버의 부담을 줄이기 위해 아주 잠깐의 간격을 둡니다 (선택 사항)
          await new Promise(resolve => setTimeout(resolve, 100)); 
        } catch (e) {
          console.error(`${word} 로드 실패:`, e);
        }
      }
      
      console.log("모든 음성 로드 완료!");
      setIsAudioReady(true);
    };
      prefetchAudios();
    }
  }, [gameState, i18n.language]);

  // 3. 볼륨 체크 로직 
  useEffect(() => {
    if (gameState === 'playing' && showVolumeCheck) {
      const startVolumeCheck = async () => {
        await playWord(t('volume_check_message'));
        setTimeout(() => {
          setShowVolumeCheck(false);
          const countdownInterval = setInterval(() => {
            setWordCountdown((prev) => {
              if (prev <= 1) {
                clearInterval(countdownInterval);
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
        }, 3000); 
      };
      startVolumeCheck();
    }
  }, [gameState, showVolumeCheck, t]);

  // 4. 단어 순차 재생 로직 (카운트다운 종료 후 실행)
  useEffect(() => {
    if (gameState === 'playing' && wordCountdown === 0 && selectedWords.length > 0) {
      const playSequence = async () => {
        const wordLang = i18n.language.startsWith('ko') ? 'ko-KR' : 'en-US';
        for (const word of selectedWords) {
          await playWord(word, wordLang);
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
        setAllWordsRead(true);
      };
      playSequence();
    }
  }, [gameState, wordCountdown, selectedWords, i18n.language]);

  // useEffect(() => {
  //   if (inputRef.current) {
  //     inputRef.current.focus();
  //   }
  // }, [allWordsRead]);

  // 5. 모든 단어 읽기가 끝난 후 타이머 시작
  useEffect(() => {
    if (allWordsRead && gameState === 'playing' && countdown > 0) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setGameState('results');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [allWordsRead, gameState, countdown]);

  useLayoutEffect(() => {
    document.documentElement.style.overflow = 'hidden'; // 잠시 스크롤 막음 (깜빡임 방지)
    document.body.style.overflow = 'hidden';
    
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;

    const timer = setTimeout(() => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const handleStart = () => {
    const player = audioPlayerRef.current;
    player.play().catch(() => {}); 
    player.pause();
    setGameState('playing');
  };

  const handleBack = () => {
    navigate(`${prefix}/cognitive/check/new`);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserInput(e.target.value);
  };

  const handleInputSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (userInput.trim()) {
      const trimmedInput = userInput.trim();
      setEnteredWords(prev => [...prev, trimmedInput]);
      
      // Check if the entered word is in the selected words list
      const normalizedInput = userInput.trim().toLowerCase();
      const isCorrect = selectedWords.some(word => word.toLowerCase() === normalizedInput);
      
      if (isCorrect && !correctWords.includes(trimmedInput)) {
        setCorrectWords(prev => [...prev, trimmedInput]);
        setScore(prev => prev + 1);
      }
      
      setUserInput('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleInputSubmit(e);
    }
  };

  const saveResultsToFirebase = async () => {
    try {
      const userId = getUserId();
      const testSetId = getCognitiveTestSetId();

      if (!userId || !testSetId) {
        console.error("사용자 ID 또는 테스트 셋 ID를 찾을 수 없습니다.");
        return;
      }

      const docId = `${userId}_${Date.now()}_${uuidv4()}`;
      const responseRef = doc(db, "userCognitiveResponse", docId);

      await setDoc(responseRef, {
        userId,
        testSetId,
        testType: "test3",
        timestamp: Timestamp.now(),
        results: {
          totalScore: score,
        },
      });

      console.log("VerbalLearning 결과 저장됨 (Firebase ID):", docId);
    } catch (error) {
      console.error("VerbalLearning 결과 저장 오류:", error);
    }
  };

  const handleSubmit = () => {
    setGameState('results');
  };

  const handleFinish = () => {
    saveResultsToFirebase();
    navigate(`${prefix}/cognitive/check/new`);
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={handleBack}>
          <ArrowBack />
        </button>
        <span className={styles.headerText}>{t('check_before_3')}</span>
      </div>

      {gameState === 'start' && (
        <React.Fragment>
        <div className={styles.startScreen}>
            <p className={styles.digitSpanInstructions1}>{t('verabl_learning_instructions_1')}</p>
            <img
              src="/images/verballearing.png"
              className={styles.instructionImage}
            />
            <p className={styles.digitSpanInstructions2}>{t('verabl_learning_instructions_2')}</p>
            <img
              src="/images/verballearning2.png"
              className={styles.instructionImage}
            />
        </div>
        <button className={styles.startButton} onClick={handleStart}>
              {t('start_test_button')}
            </button>
        </React.Fragment>
      )}

      {gameState === 'playing' && (
        <div className={styles.testScreen}>
          {showVolumeCheck ? (
            <div className={styles.volumeCheck}>
              <svg xmlns="http://www.w3.org/2000/svg" width="51" height="40" viewBox="0 0 51 40" fill="none" className={styles.speakerIcon}>
                <path d="M15.3226 9.58084L19.4393 4.44351C20.621 2.96883 23 3.80442 23 5.69417V33.9705C23 35.8955 20.547 36.7092 19.3965 35.1658L15.3226 29.7006H11C6.02944 29.7006 2 25.6712 2 20.7006V18.5808C2 13.6103 6.02944 9.58084 11 9.58084H15.3226Z" stroke="black" strokeWidth="4" strokeLinecap="round"/>
                <path d="M36 12.8809C40.3946 18.3473 39.585 21.4142 36 26.8809" stroke="black" strokeWidth="3" strokeLinecap="round"/>
                <path d="M41 2.88086C52.7188 16.5471 50.56 24.2141 41 37.8809" stroke="black" strokeWidth="3" strokeLinecap="round"/>
              </svg>
            </div>
          ) : wordCountdown > 0 ? (
            <div className={styles.countdown}>
              <p>{wordCountdown}</p>
            </div>
          ) : !allWordsRead ? (
            <div className={styles.listeningMessage}>
              <p>{t('remember_message')}</p>
              <img 
                src="/images/listen.png" 
                alt="Listen" 
                className={styles.speakerIcon}
              />
            </div>
          ) : countdown > 0 ? (
            <>
              <div className={styles.gameInfo}>
                <div className={styles.countdownTimer}>{formatTime(countdown)}</div>
              </div>
              <div className={styles.wordInputBox}>
                <div className={styles.enteredWords}>
                  {enteredWords.length > 0 ? (
                    enteredWords.map((word, index) => (
                      <span 
                        key={index} 
                        className={correctWords.includes(word) ? styles.correctWord : styles.incorrectWord}
                      >
                        {word}{index < enteredWords.length - 1 ? ', ' : ''}
                      </span>
                    ))
                  ) : (
                    <span className={styles.placeholder}></span>
                  )}
                </div>
              </div>
              
              <div className={styles.inputContainer}>
                <input
                  ref={inputRef}
                  type="text"
                  value={userInput}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter"
                  className={styles.wordInput}
                />
              </div>
              
              <button onClick={handleSubmit} className={styles.submitButton}>
                {t('submit_button')}
              </button>
            </>
          ) : (
            <div className={styles.listeningMessage}>
              <p>{t('remember_message')}</p>
              <img 
                src="/images/listen.png" 
                alt="Listen" 
                className={styles.speakerIcon}
              />
            </div>
          )}
        </div>
      )}

      {gameState === 'results' && (
        <React.Fragment>
        <div className={styles.resultsScreen}>
          <h2>{t('test_finished')}</h2>
          <div className={styles.resultsSummary}>
            <p>{t('total_success')}: {score}</p>
            <p>{t('words_entered')}: {enteredWords.join(', ')}</p>
          </div>
        </div>
        <button className={styles.finishButton} onClick={handleFinish}>
            {t('finish_button')}
        </button>
        </React.Fragment>
      )}
    </div>
  );
};

export default VerbalLearning; 